import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Shield } from "lucide-react";

export default function Login() {
  const { login, register, user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("admin@playershealth.com");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const fn = mode === "login" ? login : register;
    const res = mode === "login" ? await fn(email, password) : await fn(email, password, name);
    setLoading(false);
    if (res.ok) nav("/");
    else setError(res.error || "Failed");
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left brand panel — hero photo */}
      <div
        className="hidden lg:flex w-[50%] relative text-white p-12 flex-col justify-between bg-slate-900 overflow-hidden"
        data-testid="login-hero"
      >
        {/* photo */}
        <img
          src="/brand/login-hero.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* readability gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/30 to-slate-900/80" />

        {/* content (above photo) */}
        <div className="relative z-10 flex items-center">
          <img
            src="/brand/players-health-logo.png"
            alt="Players Health"
            className="h-8 w-auto object-contain brightness-0 invert"
          />
        </div>

        <div className="relative z-10">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight drop-shadow">
            Inception-to-date<br />MGA operations,<br />centralised.
          </h2>
          <p className="mt-4 text-white/85 text-sm max-w-md leading-relaxed drop-shadow">
            One workspace for every policy, every renewal, every broker. Upload monthly files
            and roll them up to a unified dashboard your team can trust.
          </p>
          <div className="mt-10 flex flex-wrap gap-2 text-[11px] text-white/80">
            <span className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">Underwriting</span>
            <span className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">Brokerage</span>
            <span className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">Reporting</span>
            <span className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">Reconciliation</span>
          </div>
        </div>

        <div className="relative z-10 text-[11px] text-white/70">© 2026 Players Health Cover. Internal MGA tool.</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 shadow-sm border-slate-200">
          <div className="flex items-center gap-2 mb-1 text-blue-600">
            <Shield className="w-5 h-5" strokeWidth={1.75} />
            <div className="text-[12px] uppercase tracking-wider font-medium">Secure access</div>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">
            {mode === "login"
              ? "Sign in to manage MGA production data."
              : "Set up access for a teammate."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-[12px] text-slate-600">Name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[12px] text-slate-600">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@playershealth.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[12px] text-slate-600">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10"
            >
              {loading ? "Signing in…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-5 text-center text-[12px] text-slate-500">
            {mode === "login" ? (
              <>
                Need access?{" "}
                <button
                  data-testid="switch-to-register"
                  onClick={() => setMode("register")}
                  className="text-blue-600 hover:underline"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  data-testid="switch-to-login"
                  onClick={() => setMode("login")}
                  className="text-blue-600 hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
